/* eslint-disable no-console -- this module's entire purpose is console output */
import { ClientLocationBookingConfService } from '../client_location_booking_conf.service'
import { ClientLocationService } from '../client_location.service'

/**
 * Hatch Console Diagnostics
 *
 * Prints plain-language explanations of Hatch overlay decisions to the browser
 * console on every booking search. Aimed at non-technical facility owners
 * troubleshooting rule settings.
 *
 * INTEGRATION: the service files wrap their return with `withHatchDiagnostics()`.
 *   return withHatchDiagnostics(request, { location, serviceTypeName, ... })
 *
 * TO DISABLE: replace `withHatchDiagnostics(request, ctx)` with `request`,
 *   or set `ENABLED = false` below for a kill switch.
 *
 * Design constraints:
 *   - Console only. No UI, no window API, no query flags.
 *   - One collapsed group per search. Problems print outside the group.
 *   - Never print a bare reason code — always plain language.
 *   - Always name the decider: HATCH RULES (Hatch) vs GOOSE SETTINGS (Goose).
 *   - Never throws.
 */

/* ═══════════════════════════════════════════════════════════════════════════════
   KILL SWITCH
   ═══════════════════════════════════════════════════════════════════════════════ */

const ENABLED = true

/* ═══════════════════════════════════════════════════════════════════════════════
   TYPES (self-contained)
   ═══════════════════════════════════════════════════════════════════════════════ */

interface HatchSummary {
  rulesMatched: number
  rulesSkipped: number
  offersBlocked: number
  offersRepriced: number
}

interface HatchPricingStep {
  kind: 'surcharge' | 'discount'
  ruleId: string
  label?: string
  impact: number
}

interface PricingCandidate {
  ruleId: string
  kind: 'surcharge' | 'discount'
  impact: number
}

interface PricingDayEntry {
  date: string
  baseRate: number
  surchargeRuleId?: string
  surchargeImpact?: number
  discountRuleId?: string
  discountImpact?: number
  finalRate: number
}

interface HatchOfferPricing {
  changed: boolean
  baseRate: number
  baseTotal: number
  finalRate: number
  finalTotal: number
  nights: number
  petCount: number
  delta: number
  days: PricingDayEntry[]
  steps: HatchPricingStep[]
  losingCandidates?: PricingCandidate[]
}

interface HatchOfferAvailability {
  baselineAvailable: boolean
  finalAvailable: boolean
  message?: string
  reasonType?: string
  winningRuleId?: string
  /** The action the winning rule asked for: `hide`, `block` or `waitlist`. */
  blockFlavor?: string
  /** True when the rule applied to some pets/rooms but not all. */
  partialBlock?: boolean
  affectedPets?: Array<{ locationPetProfileId?: string; id?: string; displayName?: string }>
  days: Array<{ date: string; available: boolean; winningRule?: string; reason?: string; blockFlavor?: string }>
  addOns?: Array<{ id?: string; name?: string; available?: boolean; blockingRule?: string }>
}

interface HatchOffer {
  offerId: string
  offerName: string
  changed: boolean
  availability: HatchOfferAvailability
  pricing: HatchOfferPricing
}

/**
 * True when Hatch decided this block rather than the facility.
 *
 * `source` on the reason is the normal signal, but a REQUIRED_ADDON block carries no
 * reason object at all — Hatch mirrors Goose's convention there, because the parent offer
 * has no restriction of its own; a required child offer is what's blocked. Attribution
 * then comes from the sidecar's own verdict: Goose had the offer available and Hatch took
 * it away.
 */
function isHatchDecidedBlock(
  reason: AvailabilityEntry['unavailableReason'],
  hatchOffer: HatchOffer | undefined
): boolean {
  if (isHatchSourced(reason)) return true
  // A reason that exists is authoritative: if it names Goose as the source, the facility
  // decided, even when Hatch rules also ran against this offer. Only the absence of a
  // reason licenses falling back to the sidecar verdict.
  if (reason) return false
  return hatchOffer?.availability.baselineAvailable === true && hatchOffer.availability.finalAvailable === false
}

interface HatchRule {
  ruleId: string
  status: 'matched' | 'skipped'
  area?: string
  effect?: string
  blockFlavor?: string
  affectedOffers?: Array<{ name: string; id: string }>
  matchedPets?: Array<{ id: string; displayName?: string }>
  skipReason?: string
  skipDetail?: string
}

interface HatchRuleDef {
  text: string
  area: string
  blockFlavor?: string
}

interface HatchBlock {
  evaluated: boolean
  summary: HatchSummary
  offers: HatchOffer[]
  rules: HatchRule[]
  ruleDefs: Record<string, HatchRuleDef>
}

/* ═══════════════════════════════════════════════════════════════════════════════
   CONSOLE STYLING
   ═══════════════════════════════════════════════════════════════════════════════ */

const STYLE_HEADER = 'color:#8b5cf6;font-weight:bold'
const STYLE_PROBLEM = 'color:#dc2626;font-weight:bold'
const STYLE_WARN = 'color:#d97706;font-weight:bold'
const STYLE_INFO = 'color:#0891b2'
const STYLE_DIM = 'color:#6b7280'
const STYLE_HATCH = 'color:#7c3aed;font-weight:bold'
const STYLE_GOOSE = 'color:#0d9488;font-weight:bold'

/* ═══════════════════════════════════════════════════════════════════════════════
   PLAIN-LANGUAGE MAPS
   ═══════════════════════════════════════════════════════════════════════════════ */

const REASON_TEXT: Record<string, string> = {
  RULE: 'a Hatch rule',
  WEIGHT: 'pet weight limit',
  TOTAL_WEIGHT: 'combined weight limit for the group',
  PET_COUNT: 'number of pets in the booking',
  TEMPER_TEST: 'temperament evaluation requirement',
  AGE: 'pet age limit',
  BREED: 'breed restriction',
  ALTERED: 'spay/neuter requirement',
  LEAD_TIME: 'how far ahead the booking is',
  PERIOD: 'the dates requested',
  PRICE: 'Goose price cap',
  OCCUPANCY: 'how full the location is',
  SALES_CHANNEL: 'where the booking came from',
  OPERATION_HOUR: 'opening hours',
  CHECK_IN_HOUR: 'check-in time',
  CHECK_OUT_HOUR: 'check-out time',
  MAX_BOOKING_WINDOW: 'how far ahead bookings are allowed',
  SPECIES: 'the species of pet',
  CYCLE_COUNT: 'length of stay',
  QTY: 'quantity available',
  SLOT: 'appointment slot availability',
}

function reasonText(type?: string): string {
  if (!type) return 'no reason given'
  return REASON_TEXT[type] ?? type.toLowerCase().replace(/_/g, ' ')
}

const UNAVAILABLE_TYPE_TEXT: Record<string, string> = {
  INVENTORY: 'fully booked',
  RESTRICTION: 'a booking rule blocked it',
  PRICE: 'price cap',
  REQUIRED_ADDON: 'a required add-on is unavailable',
}

const SKIP_TEXT: Record<string, { what: string; fix: string }> = {
  unsupported_condition: {
    what: 'uses a condition the engine cannot evaluate yet',
    fix: 'this rule has no effect until the engine supports it',
  },
  unsupported_scope: {
    what: 'targets something the engine cannot resolve yet',
    fix: 'this rule has no effect until the engine supports it',
  },
  unsupported_data_source: {
    what: 'needs information the system does not collect yet',
    fix: 'this rule has no effect until that data is wired up',
  },
  incomplete_data: {
    what: 'could not be checked because pet or customer data is missing',
    fix: 'the booking was BLOCKED to be safe — fill in the missing data to unblock it',
  },
  temper_test_self_gate: {
    what: 'was skipped on purpose — it would have blocked the evaluation booking itself',
    fix: 'no action needed, this is intended behaviour',
  },
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SESSION STATE
   ═══════════════════════════════════════════════════════════════════════════════ */

const ruleRegistry = new Map<string, Map<string, HatchRuleDef>>()
const addonRuleIds = new Map<string, Set<string>>()
const printedHashes = new Map<string, string>()
let currentLocation: string | null = null

function resetIfLocationChanged(location: string): void {
  if (currentLocation === location) return
  currentLocation = location
  ruleRegistry.clear()
  addonRuleIds.clear()
  printedHashes.clear()
}

/* ═══════════════════════════════════════════════════════════════════════════════
   WAITLIST SETTINGS (loaded on demand by hatch())

   A Hatch `waitlist` rule only becomes a waitlist in the booking portal when the
   location allows it: the subscriber group has waitlist on AND the service type's
   booking conf does not set waitlist to DISABLED. Otherwise the backend rejects
   the waitlist and the portal shows a regular block ("Call to Book").

   The search response doesn't carry these settings. hatch() fetches them when a
   report it is about to print contains a Hatch waitlist, so there is no cost on
   regular searches and nothing to wire up in the booking app.
   ═══════════════════════════════════════════════════════════════════════════════ */

interface HatchWaitlistSettings {
  /** `location.subscriberGroup.waitlistEnabled`. */
  subscriberGroupEnabled: boolean
  /** The service type booking conf `waitlistEnabled`: OPTIONAL, REQUIRED or DISABLED. */
  serviceTypeWaitlist?: string
}

interface WaitlistSettingsLookup {
  settings?: HatchWaitlistSettings
  error?: string
}

/** Filled by hatch() right before printing, keyed by location and service type. */
const waitlistSettings = new Map<string, WaitlistSettingsLookup>()

const settingsKey = (input: HatchDiagnosticsContext) => `${input.location}:${input.serviceTypeName}`

/** Fetch the location's waitlist settings for the service type this search was for. */
async function loadWaitlistSettings(input: HatchDiagnosticsContext, response: unknown): Promise<void> {
  const resp = response as ResponseLike | undefined
  const locationId =
    resp?.locationServiceType?.locationId ?? resp?.results?.find((r) => r.offer?.locationId)?.offer?.locationId
  const locationServiceTypeId =
    resp?.locationServiceType?.id ??
    resp?.results?.find((r) => r.offer?.locationServiceTypeId)?.offer?.locationServiceTypeId
  if (!locationId || !locationServiceTypeId) {
    waitlistSettings.set(settingsKey(input), { error: 'the response does not name its location or service type' })
    return
  }

  try {
    const [location, bookingConf] = await Promise.all([
      ClientLocationService.getLocation(input.location, locationId),
      ClientLocationBookingConfService.getConfiguration(input.location, locationServiceTypeId),
    ])
    waitlistSettings.set(settingsKey(input), {
      settings: {
        subscriberGroupEnabled: location?.subscriberGroup?.waitlistEnabled ?? false,
        serviceTypeWaitlist: bookingConf?.results?.[0]?.waitlistEnabled,
      },
    })
  } catch (error) {
    waitlistSettings.set(settingsKey(input), { error: (error as Error)?.message || 'the request failed' })
  }
}

/** Booking-portal flows that offer a waitlist. Grooming and temper test have none. */
const SERVICE_TYPES_WITHOUT_WAITLIST = new Set(['grooming', 'temper-test', 'packages'])

interface WaitlistVerdict {
  /** True when the customer can join the waitlist, null when it can't be told. */
  joins: boolean | null
  /** Plain-language reasons the waitlist did not kick in (empty when it does). */
  why: string[]
  /** True when the answer depends on settings hatch() has not loaded yet. */
  pending?: boolean
}

/** Why this kind of search can never become a waitlist, or undefined when the location settings decide. */
function waitlistNotPossible(input: HatchDiagnosticsContext): WaitlistVerdict | undefined {
  if (input.apiType === 'admin') {
    return { joins: null, why: ['admin surface: the booking portal waitlist does not apply here'] }
  }
  if (input.params?.type && input.params.type !== 'PRIMARY') {
    return { joins: false, why: ['add-ons cannot be waitlisted, only primary offers can'] }
  }
  if (SERVICE_TYPES_WITHOUT_WAITLIST.has(input.serviceTypeName)) {
    return { joins: false, why: [`${input.serviceTypeName} has no waitlist in the booking portal`] }
  }
  return undefined
}

/**
 * What the booking portal does with a Hatch `waitlist` rule in this search.
 * Mirrors the portal: the location's waitlist settings gate `waitlistEligible`
 * in components-ui.
 */
function waitlistVerdict(input: HatchDiagnosticsContext): WaitlistVerdict {
  const notPossible = waitlistNotPossible(input)
  if (notPossible) return notPossible

  const lookup = waitlistSettings.get(settingsKey(input))
  if (lookup?.error) return { joins: null, why: [`could not load the waitlist settings: ${lookup.error}`] }
  const settings = lookup?.settings
  if (!settings) return { joins: null, why: ['the waitlist settings were not loaded'], pending: true }

  const why: string[] = []
  if (!settings.subscriberGroupEnabled) why.push('waitlist is turned off for this location (subscriber group)')
  if (settings.serviceTypeWaitlist === 'DISABLED') {
    why.push(`waitlist is DISABLED in the ${input.serviceTypeName} booking settings`)
  }
  return { joins: why.length === 0, why }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   INTERNAL TYPES
   ═══════════════════════════════════════════════════════════════════════════════ */

interface AvailabilityEntry {
  available?: boolean
  unavailableReason?: {
    type?: string
    source?: string
    /** Rule id, e.g. "avail-1cb9f96c". Hatch publishes this as `name`. */
    name?: string | null
    /** The action the rule asked for: `hide`, `block` or `waitlist`. */
    blockFlavor?: string | null
    /**
     * The rule's authored message. Admin surface only — Hatch withholds it from
     * client responses because rule text names internal cohort tags.
     */
    internalDescription?: string | null
    min?: number | null
    max?: number | null
  } | null
  unavailableType?: string | null
  unavailableMsg?: string | null
  price?: { rate?: number | null; value?: number | null } | null
  affectedByPets?: Array<{ displayName?: string; weight?: number | null }> | null
}

interface SearchResultLike {
  offer?: {
    id?: string
    displayName?: string
    name?: string
    type?: string
    locationId?: string
    locationServiceTypeId?: string
  }
  hasRequiredAddons?: boolean
  availabilityGroups?: Array<{ availabilities?: AvailabilityEntry[] }>
  availabilityGroupsByDays?: Array<Array<{ availabilities?: AvailabilityEntry[] }>>
}

interface ResponseLike {
  results?: SearchResultLike[]
  hatch?: HatchBlock
  /** The location service type the search ran against (ids used to load waitlist settings). */
  locationServiceType?: { id?: string; locationId?: string }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PUBLIC API — the only thing services import
   ═══════════════════════════════════════════════════════════════════════════════ */

export interface HatchDiagnosticsContext {
  location: string
  serviceTypeName: string
  apiType: 'client' | 'admin'
  endpoint: 'search' | 'searchByDays'
  params?: {
    type?: string
    required?: boolean | string
    startDate?: string
    endDate?: string
  }
  parentOffer?: string
  pets?: Array<{ displayName?: string; weight?: number }>
}

/**
 * Wrap a search promise with diagnostics reporting.
 *
 * Usage in a service file:
 *   return withHatchDiagnostics(requester(...), { location, serviceTypeName, ... })
 *
 * The response passes through untouched. Rejections are left alone.
 * Safe to call unconditionally — does nothing if ENABLED is false or console
 * is unavailable.
 */
export function withHatchDiagnostics<T>(promise: Promise<T>, ctx: HatchDiagnosticsContext): Promise<T> {
  if (!ENABLED) return promise
  return promise.then((response) => {
    report(ctx, response)
    return response
  })
}

/**
 * Flatten request pet groups into the shape the header renderer wants.
 */
export function petsFromGroups(
  petGroups: Array<{ petCount?: number; petWeights?: number[] }> | undefined
): Array<{ displayName?: string; weight?: number }> {
  const out: Array<{ displayName?: string; weight?: number }> = []
  for (const group of petGroups ?? []) {
    const count = group.petCount ?? group.petWeights?.length ?? 0
    for (let i = 0; i < count; i++) {
      out.push({ weight: group.petWeights?.[i] })
    }
  }
  return out
}

/* ═══════════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════════ */

function firstAvailability(result: SearchResultLike): AvailabilityEntry | undefined {
  const group = result.availabilityGroups?.[0] ?? result.availabilityGroupsByDays?.[0]?.[0]
  return group?.availabilities?.[0]
}

/** True when the unavailable reason was set by the Hatch overlay, not Goose settings. */
function isHatchSourced(reason: AvailabilityEntry['unavailableReason']): boolean {
  return reason?.source === 'hatch'
}

function offerLabel(result: SearchResultLike): string {
  return result.offer?.displayName ?? result.offer?.name ?? result.offer?.id ?? '(unnamed offer)'
}

/** The action a Hatch rule stated for this entry (`hide`, `block`, `waitlist`), if any. */
function hatchFlavor(entry: AvailabilityEntry | undefined): string | undefined {
  const reason = entry?.unavailableReason
  return isHatchSourced(reason) && reason?.blockFlavor ? reason.blockFlavor : undefined
}

const FLAVOR_TEXT: Record<string, string> = {
  hide: 'hidden',
  block: 'blocked',
  waitlist: 'waitlist',
}

/** What the customer sees for an offer the first availability of which carries a Hatch action. */
function customerSeesForFlavor(flavor: string | undefined, verdict: WaitlistVerdict): string | undefined {
  if (flavor === 'hide') return 'Hidden (not shown)'
  if (flavor === 'waitlist') {
    if (verdict.joins === true) return 'Join waitlist'
    if (verdict.joins === false) return 'Call to Book (waitlist did not apply)'
    return 'Join waitlist or Call to Book (settings unknown)'
  }
  return undefined
}

/* ── Hatch actions (hide / waitlist) ──────────────────────────────────────────
   Hide and waitlist apply per pet group (room) and per day, so they can't be read
   from the first availability alone the way blocks are. These helpers walk every
   entry and say where each action landed.
   ──────────────────────────────────────────────────────────────────────────── */

interface ActionHit {
  flavor: string
  ruleId?: string
  /** Room (or pet, for grooming) index, 0-based. */
  group: number
  /** Day index for /days responses, undefined otherwise. */
  day?: number
  pets: string[]
}

interface OfferActions {
  result: SearchResultLike
  hits: ActionHit[]
  /** How many groups and days the offer has, to tell "everywhere" from "some". */
  groupCount: number
  dayCount: number
}

function collectActionHits(result: SearchResultLike): OfferActions {
  const hits: ActionHit[] = []
  const addHit = (entry: AvailabilityEntry | undefined, group: number, day?: number) => {
    const flavor = hatchFlavor(entry)
    if (flavor !== 'hide' && flavor !== 'waitlist') return
    hits.push({
      flavor,
      ruleId: entry?.unavailableReason?.name ?? undefined,
      group,
      day,
      pets: (entry?.affectedByPets ?? []).map((p) => p.displayName ?? '').filter(Boolean),
    })
  }

  const byRoom = result.availabilityGroups ?? []
  byRoom.forEach((group, groupIdx) => (group?.availabilities ?? []).forEach((entry) => addHit(entry, groupIdx)))

  const byDay = result.availabilityGroupsByDays ?? []
  byDay.forEach((dayGroups, dayIdx) =>
    (dayGroups ?? []).forEach((group, groupIdx) =>
      (group?.availabilities ?? []).forEach((entry) => addHit(entry, groupIdx, dayIdx))
    )
  )

  return {
    result,
    hits,
    groupCount: byRoom.length || (byDay[0]?.length ?? 0),
    dayCount: byDay.length,
  }
}

/* ── Per-day model ────────────────────────────────────────────────────────────
   A /days response carries one entry per calendar day in `availabilityGroupsByDays`,
   and the sidecar mirrors it in `availability.days[]` with the date and winning rule.
   Reading only day 0 (as a single-availability response would) hides every partial
   block — the most common daycare case. These helpers keep the day axis intact.
   ──────────────────────────────────────────────────────────────────────────── */

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Render `2026-08-20` as `Thu Aug 20`.
 *
 * Parsed as UTC on purpose: these are plain calendar dates, and local-time
 * parsing would shift them a day for anyone west of UTC — which would rename
 * the weekday and make a "blocked on Thursdays" rule look wrong.
 */
function formatDay(date: string | undefined): string {
  if (!date) return '(unknown date)'
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  return `${WEEKDAY_NAMES[parsed.getUTCDay()]} ${MONTH_NAMES[parsed.getUTCMonth()]} ${parsed.getUTCDate()}`
}

/** Add `offset` days to a YYYY-MM-DD string. Used when the sidecar omits dates. */
function addDays(start: string | undefined, offset: number): string | undefined {
  if (!start) return undefined
  const parsed = new Date(`${start}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return undefined
  parsed.setUTCDate(parsed.getUTCDate() + offset)
  return parsed.toISOString().slice(0, 10)
}

interface DayVerdict {
  index: number
  date?: string
  available: boolean
  reasonType?: string
  /** True when the Hatch overlay decided this day, rather than the facility. */
  isHatch: boolean
  ruleId?: string
  /** The action the Hatch rule stated for this day: `hide`, `block` or `waitlist`. */
  flavor?: string
}

/**
 * Build the per-day verdict list, or null when this is not a /days response.
 *
 * Availability comes from the response (what the customer actually gets); the
 * date and winning rule come from the sidecar (what the engine decided). They
 * are zipped by index, and only when the lengths agree — a mismatch means we
 * cannot trust the pairing, so dates degrade to being derived from startDate.
 */
function collectDayVerdicts(
  result: SearchResultLike,
  hatchOffer: HatchOffer | undefined,
  startDate: string | undefined
): DayVerdict[] | null {
  const byDays = result.availabilityGroupsByDays
  if (!byDays || byDays.length === 0) return null

  const sidecarDays = hatchOffer?.availability.days ?? []
  const aligned = sidecarDays.length === byDays.length

  return byDays.map((group, index) => {
    const entry = group?.[0]?.availabilities?.[0]
    const sidecarDay = aligned ? sidecarDays[index] : undefined
    const reason = entry?.unavailableReason
    return {
      index,
      date: sidecarDay?.date ?? addDays(startDate, index),
      available: Boolean(entry?.available),
      reasonType: reason?.type ?? sidecarDay?.reason,
      // The response's `source` is the primary signal; a winning rule on the
      // sidecar day is a fallback for when the two arrays could not be zipped.
      isHatch: isHatchSourced(reason) || Boolean(sidecarDay?.winningRule),
      // Hatch now carries the rule id on the reason itself, so a day can be
      // attributed even when the sidecar arrays could not be zipped.
      ruleId: sidecarDay?.winningRule ?? reason?.name ?? undefined,
      flavor: hatchFlavor(entry) ?? sidecarDay?.blockFlavor,
    }
  })
}

type OfferState = 'available' | 'partial' | 'blocked'

/**
 * Classify an offer across the whole requested range.
 *
 * For /days responses this is a day-count decision, not a day-0 lookup: an offer
 * with some open days is bookable ('partial'), and only an offer with no open day
 * at all is 'blocked'.
 */
function offerState(result: SearchResultLike, days: DayVerdict[] | null): OfferState {
  if (days && days.length > 0) {
    const open = days.filter((d) => d.available).length
    if (open === 0) return 'blocked'
    return open < days.length ? 'partial' : 'available'
  }
  return firstAvailability(result)?.available ? 'available' : 'blocked'
}

function hashReport(hatch: HatchBlock | undefined, results: SearchResultLike[]): string {
  const summary = hatch
    ? `${hatch.evaluated}:${hatch.summary.rulesMatched}:${hatch.summary.rulesSkipped}:${hatch.summary.offersBlocked}:${hatch.summary.offersRepriced}`
    : 'none'
  // Day-aware: a change in which days are blocked must defeat the dedupe, or a
  // partial block that shifts dates would print once and never update.
  const verdicts = results
    .map((r) => {
      const byDays = r.availabilityGroupsByDays
      // The Hatch action is part of the verdict, so a block that becomes a hide
      // or a waitlist is reported again rather than deduped away.
      const cell = (entry: AvailabilityEntry | undefined) =>
        `${entry?.available ? 1 : 0}${hatchFlavor(entry)?.[0] ?? ''}`
      if (byDays && byDays.length > 0) {
        const pattern = byDays.map((g) => cell(g?.[0]?.availabilities?.[0])).join('')
        return `${r.offer?.id ?? '?'}:${pattern}`
      }
      const rooms = (r.availabilityGroups ?? []).map((g) => cell(g?.availabilities?.[0])).join('')
      return `${r.offer?.id ?? '?'}:${rooms || cell(firstAvailability(r))}`
    })
    .join('|')
  return `${summary}#${verdicts}`
}

interface ActionSummary {
  /** Offers with at least one entry hidden by Hatch. */
  hidden: number
  /** Offers with at least one entry waitlisted by Hatch. */
  waitlisted: number
  waitlist: WaitlistVerdict
}

function summarizeActions(input: HatchDiagnosticsContext, results: SearchResultLike[]): ActionSummary {
  const all = results.map(collectActionHits)
  return {
    hidden: all.filter((a) => a.hits.some((h) => h.flavor === 'hide')).length,
    waitlisted: all.filter((a) => a.hits.some((h) => h.flavor === 'waitlist')).length,
    waitlist: waitlistVerdict(input),
  }
}

function buildHeader(
  input: HatchDiagnosticsContext,
  hatch: HatchBlock | undefined,
  blockedCount: number,
  partialCount: number,
  ruleBlockedDays: number,
  actions?: ActionSummary
): string {
  const bits: string[] = [input.serviceTypeName]

  if (input.params?.type) bits.push(input.params.type)
  if (input.params?.required === true || input.params?.required === 'true') bits.push('required')
  if (input.parentOffer) bits.push(`parent=${input.parentOffer}`)
  if (input.endpoint === 'searchByDays') bits.push('/days')

  const dates =
    input.params?.startDate && input.params?.endDate
      ? `${input.params.startDate} \u2192 ${input.params.endDate}`
      : 'no dates'
  bits.push(dates)

  const pets = input.pets ?? []
  if (pets.length > 0) {
    const weights = pets.map((p) => p.weight).filter((w): w is number => w != null)
    bits.push(
      weights.length > 0
        ? `${pets.length} pet${pets.length === 1 ? '' : 's'} (${weights.join(', ')} lbs)`
        : `${pets.length} pet${pets.length === 1 ? '' : 's'}`
    )
  }

  if (!hatch) {
    bits.push('no hatch sidecar')
  } else if (!hatch.evaluated) {
    bits.push('no rules for this location')
  } else {
    bits.push(`${hatch.summary.rulesMatched} rule${hatch.summary.rulesMatched === 1 ? '' : 's'} fired`)
    if (hatch.summary.rulesSkipped > 0) bits.push(`${hatch.summary.rulesSkipped} skipped`)
    if (blockedCount > 0) bits.push(`${blockedCount} offer${blockedCount === 1 ? '' : 's'} blocked`)
    // Partial blocking is the normal daycare outcome and must be visible in the
    // collapsed header, otherwise the only clue is a greyed-out calendar cell.
    if (partialCount > 0) bits.push(`${partialCount} partly blocked`)
    if (ruleBlockedDays > 0) {
      bits.push(`${ruleBlockedDays} day${ruleBlockedDays === 1 ? '' : 's'} blocked by Hatch rules`)
    }
    if (actions && actions.hidden > 0) bits.push(`${actions.hidden} hidden`)
    if (actions && actions.waitlisted > 0) {
      // Say up front when a waitlist rule fired but the portal shows a block instead.
      // The always-on line runs before hatch() has loaded the location settings.
      const outcome =
        actions.waitlist.joins === false
          ? ' (shown as Call to Book)'
          : actions.waitlist.pending
            ? ' (type hatch() to check if it applies)'
            : actions.waitlist.joins === null
              ? ' (settings unknown)'
              : ''
      bits.push(`${actions.waitlisted} waitlist${outcome}`)
    }
    if (hatch.summary.offersRepriced > 0) bits.push(`${hatch.summary.offersRepriced} repriced`)
  }

  return bits.join(' \u00B7 ')
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SECTION RENDERERS
   ═══════════════════════════════════════════════════════════════════════════════ */

function printCustomerView(
  results: SearchResultLike[],
  hatch: HatchBlock | undefined,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>,
  verdict: WaitlistVerdict
): void {
  const rows = results.map((result) => {
    const availability = firstAvailability(result)
    const days = dayMap.get(result) ?? null
    const state = offerState(result, days)
    const reason = availability?.unavailableReason
    const hatchOffer = hatch?.offers.find((o) => o.offerId === result.offer?.id)

    // Who decided. For a partial block the deciders can differ per day, so the
    // column reports whichever layers contributed rather than a single verdict.
    let decidedBy = '\u2014'
    if (state === 'partial' && days) {
      const blockedDays = days.filter((d) => !d.available)
      const byRules = blockedDays.filter((d) => d.isHatch).length
      const byFacility = blockedDays.length - byRules
      const parts: string[] = []
      if (byRules > 0) parts.push(`Hatch rules (${byRules}d)`)
      if (byFacility > 0) parts.push(`Goose (${byFacility}d)`)
      decidedBy = parts.join(' + ')
    } else if (state === 'blocked') {
      const blockedByHatch = days ? days.some((d) => d.isHatch) : isHatchDecidedBlock(reason, hatchOffer)
      if (blockedByHatch) {
        const ruleId = hatchOffer?.availability.winningRuleId ?? days?.find((d) => d.isHatch)?.ruleId
        decidedBy = ruleId ? `Hatch rule (${ruleId})` : 'Hatch rules'
      } else {
        const reasonType = reason?.type ?? days?.find((d) => !d.available)?.reasonType
        decidedBy = `Goose setting (${reasonText(reasonType)})`
      }
    }

    const rate = availability?.price?.rate
    const priceShown =
      state === 'blocked' || reason?.type === 'PRICE' ? '\u2014' : rate != null ? `$${rate}` : 'no price'

    // Hatch actions change what a blocked offer looks like: a hide removes it, a
    // waitlist offers to join (when the location allows it). By-day offers stay
    // selectable when a waitlist day applies; the date picker handles those days.
    const blockedFlavor = days
      ? days.every((d) => d.flavor === 'hide')
        ? 'hide'
        : days.some((d) => d.flavor === 'waitlist')
          ? 'waitlist'
          : undefined
      : hatchFlavor(availability)
    const flavorSees = state === 'blocked' ? customerSeesForFlavor(blockedFlavor, verdict) : undefined
    const byDayWaitlist = days && blockedFlavor === 'waitlist' && verdict.joins === true
    const customerSees = byDayWaitlist
      ? 'Select (waitlist days in the date picker)'
      : (flavorSees ??
        (state === 'available' ? 'Select' : state === 'partial' ? 'Select (some days unavailable)' : 'Call to Book'))

    const row: Record<string, string> = {
      Offer: offerLabel(result),
      Customer_sees: customerSees,
      Price: priceShown,
      Decided_by: decidedBy,
    }

    if (days) {
      const open = days.filter((d) => d.available).length
      row.Days_open = `${open} of ${days.length}`
    }

    return row
  })

  if (rows.length === 0) {
    console.log('%c(no offers returned)', STYLE_DIM)
    return
  }
  console.log('%c\u{1F4CB} WHAT THE CUSTOMER SEES', STYLE_HEADER)
  console.table(rows)
}

/**
 * Day-by-day breakdown for /days searches, grouped by what caused each block.
 *
 * Grouping by cause rather than listing dates chronologically is deliberate: it
 * makes a recurring pattern ("all four Thursdays") legible at a glance, which is
 * exactly the check an owner needs when verifying a weekday rule.
 */
function printDayBreakdown(
  results: SearchResultLike[],
  hatch: HatchBlock,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>
): void {
  const withDays = results.filter((r) => {
    const days = dayMap.get(r)
    return days && days.some((d) => !d.available)
  })
  if (withDays.length === 0) return

  console.groupCollapsed('%c\u{1F4C5} DAY-BY-DAY AVAILABILITY', STYLE_HEADER)

  for (const result of withDays) {
    const days = dayMap.get(result)!
    const blocked = days.filter((d) => !d.available)
    const open = days.filter((d) => d.available)

    console.group(
      `%c${offerLabel(result)}%c \u2014 ${open.length} of ${days.length} days open`,
      'font-weight:bold',
      STYLE_DIM
    )

    // Your rules, grouped by rule id so a recurring pattern reads as one entry.
    const ruleBlocked = blocked.filter((d) => d.isHatch)
    if (ruleBlocked.length > 0) {
      console.log(`%cBLOCKED BY HATCH RULES \u2014 ${ruleBlocked.length} day(s)`, STYLE_HATCH)
      const byRule = new Map<string, DayVerdict[]>()
      for (const day of ruleBlocked) {
        const key = day.ruleId ?? '(rule not identified)'
        const list = byRule.get(key)
        if (list) list.push(day)
        else byRule.set(key, [day])
      }
      for (const [ruleId, ruleDays] of byRule) {
        const def = hatch.ruleDefs[ruleId]
        const flavor = ruleDays[0]?.flavor ?? def?.blockFlavor
        const flavorLabel = flavor && FLAVOR_TEXT[flavor] ? ` [${FLAVOR_TEXT[flavor]}]` : ''
        console.log(`  ${ruleId}${flavorLabel}${def ? ` \u2014 "${def.text}"` : ''}`)
        console.log(`    \u2192 ${ruleDays.map((d) => formatDay(d.date)).join(', ')}`)
      }
    }

    // Facility settings, grouped by reason type.
    const facilityBlocked = blocked.filter((d) => !d.isHatch)
    if (facilityBlocked.length > 0) {
      console.log(`%cBLOCKED BY GOOSE SETTINGS \u2014 ${facilityBlocked.length} day(s)`, STYLE_GOOSE)
      const byReason = new Map<string, DayVerdict[]>()
      for (const day of facilityBlocked) {
        const key = day.reasonType ?? 'unknown'
        const list = byReason.get(key)
        if (list) list.push(day)
        else byReason.set(key, [day])
      }
      for (const [reasonType, reasonDays] of byReason) {
        console.log(`  ${reasonText(reasonType)}`)
        console.log(`    \u2192 ${reasonDays.map((d) => formatDay(d.date)).join(', ')}`)
      }
    }

    if (open.length > 0) {
      console.log(`%cOPEN \u2014 ${open.length} day(s)`, STYLE_DIM)
      console.log(`%c  ${open.map((d) => formatDay(d.date)).join(', ')}`, STYLE_DIM)
    }

    console.groupEnd()
  }

  console.groupEnd()
}

function printBlockedOffers(
  results: SearchResultLike[],
  hatch: HatchBlock | undefined,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>,
  apiType: 'client' | 'admin',
  verdict: WaitlistVerdict
): void {
  // Only offers with no open day at all. Partially blocked offers are still
  // bookable and are covered by the day-by-day section instead.
  const blocked = results.filter((r) => offerState(r, dayMap.get(r) ?? null) === 'blocked')
  if (blocked.length === 0) return

  console.groupCollapsed(`%c\u{1F6AB} BLOCKED OFFERS (${blocked.length})`, STYLE_HEADER)
  for (const result of blocked) {
    const availability = firstAvailability(result)
    const reason = availability?.unavailableReason
    const hatchOffer = hatch?.offers.find((o) => o.offerId === result.offer?.id)
    const isHatch = isHatchDecidedBlock(reason, hatchOffer)
    const flavor = hatchFlavor(availability)
    const label = customerSeesForFlavor(flavor, verdict) ?? 'Call to Book'

    console.group(`%c${offerLabel(result)}%c \u2014 "${label}"`, 'font-weight:bold', STYLE_DIM)
    if (flavor) console.log(`Action: ${FLAVOR_TEXT[flavor] ?? flavor} (Hatch rule)`)

    const unavailableType = availability?.unavailableType
    if (unavailableType && UNAVAILABLE_TYPE_TEXT[unavailableType]) {
      console.log(`Why:    ${UNAVAILABLE_TYPE_TEXT[unavailableType]}`)
    } else {
      console.log(`Why:    ${reasonText(reason?.type)}`)
    }

    const blockedAddon = hatchOffer?.availability.addOns?.find((a) => a.available === false)
    if (unavailableType === 'REQUIRED_ADDON' && blockedAddon) {
      const addonName = blockedAddon.name ?? blockedAddon.id
      const blockingRule = blockedAddon.blockingRule
      console.log(
        `Chain:  Hatch rule blocked "${addonName}" \u2192 "${addonName}" is required on this offer \u2192 the offer cannot be booked`
      )
      if (blockingRule && hatch?.ruleDefs[blockingRule]) {
        console.log(`Rule:   ${blockingRule}\n        "${hatch.ruleDefs[blockingRule].text}"`)
      }
    } else if (isHatch) {
      // Prefer the sidecar id, but fall back to the id Hatch now stamps on the
      // reason itself so a block stays attributable when the lookup misses.
      const ruleId = hatchOffer?.availability.winningRuleId ?? reason?.name
      if (ruleId) {
        // `ruleDefs` is the primary source for the authored text; `internalDescription`
        // carries the same text on the entry, so it keeps the rule readable when the
        // sidecar lookup misses.
        const ruleText = hatch?.ruleDefs[ruleId]?.text ?? reason?.internalDescription
        console.log(`Rule:   ${ruleId}${ruleText ? `\n        "${ruleText}"` : ''}`)
      }
    }

    // `min`/`max` bound the range in which the offer is UNAVAILABLE, and the
    // range is half-open: [min, max). Spelling that out avoids reading `min` as
    // a floor the booking has to clear.
    if (reason?.min != null || reason?.max != null) {
      const range =
        reason.min != null && reason.max != null
          ? `blocked from ${reason.min} up to ${reason.max}`
          : reason.min != null
            ? `blocked at ${reason.min} and above`
            : `blocked below ${reason.max}`
      console.log(`Range:  ${range}`)
    }

    const pets = availability?.affectedByPets ?? hatchOffer?.availability.affectedPets
    if (pets && pets.length > 0) {
      const names = pets
        .map((p) => {
          const weight = (p as { weight?: number | null }).weight
          return weight != null ? `${p.displayName} (${weight} lbs)` : p.displayName
        })
        .join(', ')
      console.log(`Pets:   ${names}`)
    }

    const days = hatchOffer?.availability.days ?? []
    if (days.length > 0) {
      const blockedDays = days.filter((d) => !d.available)
      if (blockedDays.length === days.length) {
        console.log(`Days:   all ${days.length} blocked`)
      } else if (blockedDays.length > 0) {
        console.log(
          `Days:   ${blockedDays.length} of ${days.length} blocked \u2014 ${blockedDays.map((d) => d.date).join(', ')}`
        )
      }
    }

    // `unavailableMsg` holds the rule's LLM-generated block message. Deliberately NOT
    // `internalDescription`, which carries the rule's authored text and is already printed
    // by the `Rule:` line above — they are different things and conflating them here would
    // report the rule as its own explanation.
    //
    // On the client surface Goose overwrites this field with its own generic constant, so
    // there is no rule message to show and printing it would misreport whose copy it is.
    if (isHatch && apiType === 'client') {
      console.log(`Message: (withheld on the customer surface \u2014 see the rule text above)`)
    } else if (availability?.unavailableMsg) {
      console.log(`Message: "${availability.unavailableMsg}"`)
    }

    console.log(
      isHatch ? '%cDecided by: HATCH RULES' : '%cDecided by: GOOSE SETTINGS',
      isHatch ? STYLE_HATCH : STYLE_GOOSE
    )
    console.groupEnd()
  }
  console.groupEnd()
}

/** "room 2", "all rooms", "Thu Aug 20, Thu Aug 27", "all 15 days"… */
function describeWhere(
  actions: OfferActions,
  hits: ActionHit[],
  days: DayVerdict[] | null,
  groupNoun: 'room' | 'pet'
): string {
  if (actions.dayCount > 0) {
    const dayIdx = [...new Set(hits.map((h) => h.day ?? 0))].sort((a, b) => a - b)
    const dayText =
      dayIdx.length === actions.dayCount
        ? `on all ${actions.dayCount} day${actions.dayCount === 1 ? '' : 's'}`
        : `on ${dayIdx.map((i) => formatDay(days?.[i]?.date)).join(', ')}`
    const groups = [...new Set(hits.map((h) => h.group))]
    const groupText =
      actions.groupCount > 1 && groups.length < actions.groupCount
        ? ` for ${groups.map((g) => `${groupNoun} ${g + 1}`).join(', ')}`
        : ''
    return `${dayText}${groupText}`
  }
  const groups = [...new Set(hits.map((h) => h.group))].sort((a, b) => a - b)
  if (actions.groupCount <= 1) return 'for this booking'
  if (groups.length === actions.groupCount) return `in all ${actions.groupCount} ${groupNoun}s`
  return `in ${groups.map((g) => `${groupNoun} ${g + 1}`).join(', ')}`
}

/** True when every entry of the offer carries this action (nothing else left to show). */
function coversEverything(actions: OfferActions, flavor: string): boolean {
  const entries: AvailabilityEntry[] = []
  for (const group of actions.result.availabilityGroups ?? []) entries.push(...(group?.availabilities ?? []))
  for (const day of actions.result.availabilityGroupsByDays ?? [])
    for (const group of day ?? []) entries.push(...(group?.availabilities ?? []))
  return entries.length > 0 && entries.every((e) => hatchFlavor(e) === flavor)
}

/**
 * Hide and waitlist outcomes, per offer. Blocks are already covered by
 * BLOCKED OFFERS; hide and waitlist need their own view because they apply per
 * room and per day, and a waitlist can silently turn into a block when the
 * location's waitlist settings are off.
 */
function printHatchActions(
  input: HatchDiagnosticsContext,
  results: SearchResultLike[],
  hatch: HatchBlock,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>,
  verdict: WaitlistVerdict
): void {
  const all = results.map(collectActionHits).filter((a) => a.hits.length > 0)
  if (all.length === 0) return

  const groupNoun = input.serviceTypeName === 'grooming' ? 'pet' : 'room'
  const isByDay = input.endpoint === 'searchByDays'
  const isAddOn = !!input.params?.type && input.params.type !== 'PRIMARY'

  const printRule = (ruleId: string | undefined) => {
    if (!ruleId) return
    const text = hatch.ruleDefs[ruleId]?.text
    console.log(`Rule:   ${ruleId}${text ? `\n        "${text}"` : ''}`)
  }
  const printPets = (hits: ActionHit[]) => {
    const pets = [...new Set(hits.flatMap((h) => h.pets))]
    if (pets.length > 0) console.log(`Pets:   ${pets.join(', ')}`)
  }

  for (const flavor of ['hide', 'waitlist'] as const) {
    const withFlavor = all.filter((a) => a.hits.some((h) => h.flavor === flavor))
    if (withFlavor.length === 0) continue

    const title =
      flavor === 'hide'
        ? `%c\u{1F648} HIDDEN BY HATCH RULES (${withFlavor.length})`
        : `%c\u23F3 WAITLIST BY HATCH RULES (${withFlavor.length})`
    console.groupCollapsed(title, STYLE_HEADER)

    for (const actions of withFlavor) {
      const days = dayMap.get(actions.result) ?? null
      const byRule = new Map<string, ActionHit[]>()
      for (const hit of actions.hits.filter((h) => h.flavor === flavor)) {
        const key = hit.ruleId ?? '(rule not identified)'
        const list = byRule.get(key)
        if (list) list.push(hit)
        else byRule.set(key, [hit])
      }

      for (const [ruleId, hits] of byRule) {
        console.group(
          `%c${offerLabel(actions.result)}%c \u2014 ${describeWhere(actions, hits, days, groupNoun)}`,
          'font-weight:bold',
          STYLE_DIM
        )
        printRule(ruleId === '(rule not identified)' ? undefined : ruleId)
        printPets(hits)

        const everywhere = coversEverything(actions, flavor)
        if (flavor === 'hide') {
          let sees: string
          if (everywhere) sees = 'nothing, the offer is removed from the list'
          else if (isByDay && groupNoun === 'pet')
            sees = 'the groom stays in the list; it is removed only when every pet in the room is hidden'
          else if (isByDay) sees = 'those days are disabled in the date picker, like a blocked day'
          else if (isAddOn)
            sees = `the add-on is removed from that ${groupNoun}'s tab; hidden pets drop out of its pet picker`
          else sees = `the offer is removed from that ${groupNoun}'s tab; other ${groupNoun}s still see it`
          console.log(`Customer sees: ${sees}`)
        } else {
          if (verdict.joins === true) {
            console.log(
              `Customer sees: ${isByDay ? 'those days can be picked as waitlist-only in the date picker' : 'a "Join waitlist" button'}`
            )
          } else {
            console.log(
              `%cCustomer sees: ${verdict.joins === false ? '"Call to Book"' : 'unknown'} \u2014 the waitlist did not apply because:`,
              STYLE_WARN
            )
            for (const why of verdict.why) console.log(`  \u2022 ${why}`)
          }
        }
        console.groupEnd()
      }
    }
    console.groupEnd()
  }
}

function printAvailableOffers(
  results: SearchResultLike[],
  hatch: HatchBlock | undefined,
  dayMap: Map<SearchResultLike, DayVerdict[] | null>
): void {
  const bookable = results.filter((r) => offerState(r, dayMap.get(r) ?? null) !== 'blocked')
  if (bookable.length === 0) return

  console.groupCollapsed(`%c\u2705 BOOKABLE OFFERS (${bookable.length})`, STYLE_HEADER)
  for (const result of bookable) {
    const hatchOffer = hatch?.offers.find((o) => o.offerId === result.offer?.id)
    const rate = firstAvailability(result)?.price?.rate
    const days = dayMap.get(result) ?? null
    const notes: string[] = []

    // Say up front that a bookable offer still has holes, and how many are ours.
    if (offerState(result, days) === 'partial' && days) {
      const blockedDays = days.filter((d) => !d.available)
      const byRules = blockedDays.filter((d) => d.isHatch).length
      notes.push(
        byRules > 0
          ? `${blockedDays.length} of ${days.length} days unavailable, ${byRules} because of Hatch rules`
          : `${blockedDays.length} of ${days.length} days unavailable, all Goose settings`
      )
    }
    if (hatchOffer?.availability.baselineAvailable === false) notes.push('unlocked by one of your allow rules')
    if (result.hasRequiredAddons) notes.push('has required add-ons')

    console.log(
      `${offerLabel(result)}${rate != null ? ` \u2014 $${rate}` : ''}${notes.length ? ` (${notes.join('; ')})` : ''}`
    )
  }
  console.groupEnd()
}

function printPricing(hatch: HatchBlock, apiType: 'client' | 'admin'): void {
  const repriced = hatch.offers.filter((o) => o.pricing?.changed)

  console.groupCollapsed(
    `%c\u{1F4B0} PRICING (${repriced.length} offer${repriced.length === 1 ? '' : 's'} adjusted)`,
    STYLE_HEADER
  )

  if (apiType === 'client') {
    console.log(
      '%c\u2139\uFE0F On the customer booking page, prices are CALCULATED but NOT applied. The figures below are what Hatch rules would do \u2014 the customer still sees the Goose price.',
      STYLE_INFO
    )
  }

  if (repriced.length === 0) {
    console.log('%cNo pricing rules changed any price in this search.', STYLE_DIM)
  }

  for (const offer of repriced) {
    const p = offer.pricing
    console.group(`%c${offer.offerName}`, 'font-weight:bold')
    console.log(`Goose price: $${p.baseRate}/night \u00B7 $${p.baseTotal} total`)
    console.log(
      `With Hatch rules: $${p.finalRate}/night \u00B7 $${p.finalTotal} total (${p.delta >= 0 ? '+' : ''}$${p.delta})`
    )
    if (p.nights != null || p.petCount != null) {
      const parts: string[] = []
      if (p.nights != null) parts.push(`${p.nights} night${p.nights === 1 ? '' : 's'}`)
      if (p.petCount != null) parts.push(`${p.petCount} pet${p.petCount === 1 ? '' : 's'}`)
      console.log(`%c  (${parts.join(', ')})`, STYLE_DIM)
    }
    for (const step of p.steps ?? []) {
      const sign = step.kind === 'surcharge' ? '+' : '\u2212'
      console.log(`  ${step.kind}: ${sign}$${Math.abs(step.impact)} \u2014 ${step.label ?? step.ruleId}`)
    }
    if (p.losingCandidates && p.losingCandidates.length > 0) {
      console.log(
        `%c  ${p.losingCandidates.length} other pricing rule(s) matched but lost (highest amount wins): ${p.losingCandidates.map((c) => c.ruleId).join(', ')}`,
        STYLE_DIM
      )
    }
    // Per-day pricing breakdown — shows which rules fire on which days
    if (p.days && p.days.length > 1) {
      const hasVariance = p.days.some((d) => d.finalRate !== p.finalRate || d.surchargeRuleId || d.discountRuleId)
      if (hasVariance) {
        console.groupCollapsed(`%c  Per-day breakdown (${p.days.length} days)`, STYLE_DIM)
        for (const day of p.days) {
          const parts: string[] = [`$${day.baseRate}`]
          if (day.surchargeRuleId) parts.push(`+$${day.surchargeImpact} (${day.surchargeRuleId})`)
          if (day.discountRuleId) parts.push(`\u2212$${Math.abs(day.discountImpact ?? 0)} (${day.discountRuleId})`)
          parts.push(`\u2192 $${day.finalRate}`)
          console.log(`  ${day.date}: ${parts.join(' ')}`)
        }
        console.groupEnd()
      }
    }
    console.groupEnd()
  }
  console.groupEnd()
}

/**
 * Map offer id → customer-facing name.
 *
 * The sidecar carries the internal slug (`no-tt-daycare`) while the response has
 * the display name (`No TT Daycare`). Owners recognise the latter, so rule output
 * is resolved through this rather than echoing the slug.
 */
function displayNameIndex(results: SearchResultLike[]): Map<string, string> {
  const index = new Map<string, string>()
  for (const result of results) {
    const id = result.offer?.id
    const name = result.offer?.displayName
    if (id && name) index.set(id, name)
  }
  return index
}

function printRulesFired(hatch: HatchBlock, names: Map<string, string>): void {
  const fired = hatch.rules.filter((r) => r.status === 'matched')
  if (fired.length === 0) {
    console.log('%c\u{1F4D6} NO RULES FIRED in this search', STYLE_DIM)
    return
  }

  console.groupCollapsed(`%c\u{1F4D6} RULES THAT FIRED (${fired.length})`, STYLE_HEADER)
  for (const rule of fired) {
    const def = hatch.ruleDefs[rule.ruleId]
    console.group(`%c${rule.ruleId}`, 'font-weight:bold')
    if (def) console.log(`"${def.text}"`)
    const flavor = rule.blockFlavor ?? def?.blockFlavor
    console.log(`Effect: ${rule.effect ?? '?'}${flavor ? ` \u00B7 ${flavor}` : ''} (${rule.area ?? '?'})`)
    if (rule.affectedOffers?.length)
      console.log(`Applied to: ${rule.affectedOffers.map((o) => names.get(o.id) ?? o.name ?? o.id).join(', ')}`)
    if (rule.matchedPets?.length)
      console.log(`Triggered by: ${rule.matchedPets.map((p) => p.displayName ?? p.id).join(', ')}`)
    console.groupEnd()
  }
  console.groupEnd()
}

function printRulesSkipped(hatch: HatchBlock): void {
  const skipped = hatch.rules.filter((r) => r.status === 'skipped')
  if (skipped.length === 0) return

  console.groupCollapsed(`%c\u23ED\uFE0F RULES THE ENGINE COULD NOT CHECK (${skipped.length})`, STYLE_WARN)
  for (const rule of skipped) {
    const def = hatch.ruleDefs[rule.ruleId]
    const explain = rule.skipReason ? SKIP_TEXT[rule.skipReason] : undefined
    console.group(`%c${rule.ruleId}`, 'font-weight:bold')
    if (def) console.log(`"${def.text}"`)
    if (explain) {
      console.log(`Problem: this rule ${explain.what}`)
      console.log(`Effect:  ${explain.fix}`)
    } else {
      console.log(`Problem: ${rule.skipReason ?? 'unknown'}`)
    }
    if (rule.skipDetail) console.log(`%cDetail:  ${rule.skipDetail}`, STYLE_DIM)
    console.groupEnd()
  }
  console.groupEnd()
}

function printRulesThatDidNothing(location: string, hatch: HatchBlock, requestType?: string): void {
  const known = ruleRegistry.get(location)
  if (!known || known.size === 0) return

  const active = new Set(hatch.rules.map((r) => r.ruleId))
  const idle = [...known.entries()].filter(([ruleId]) => !active.has(ruleId))
  if (idle.length === 0) return

  const locationAddonRules = addonRuleIds.get(location) ?? new Set<string>()
  const isPrimaryRequest = !requestType || requestType === 'PRIMARY'

  console.groupCollapsed(`%c\u{1F47B} RULES THAT DID NOTHING IN THIS SEARCH (${idle.length})`, STYLE_DIM)
  console.log(
    '%cThese rules exist but changed nothing here. Seen in earlier searches this session \u2014 the response itself does not list them.',
    STYLE_DIM
  )
  for (const [ruleId, def] of idle) {
    console.group(`%c${ruleId}`, 'font-weight:bold')
    console.log(`"${def.text}"`)
    if (locationAddonRules.has(ruleId) && isPrimaryRequest) {
      console.log('Why: this rule targets an add-on, so it only applies at the add-on step of the booking')
    } else {
      console.log('Why: no pet or offer in this search matched its conditions, or it targets a different offer')
    }
    console.groupEnd()
  }
  console.groupEnd()
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PROBLEMS (printed outside the group)
   ═══════════════════════════════════════════════════════════════════════════════ */

function collectProblems(hatch: HatchBlock): Array<{ level: 'problem' | 'warn'; message: string }> {
  const out: Array<{ level: 'problem' | 'warn'; message: string }> = []

  for (const rule of hatch.rules) {
    if (rule.status !== 'skipped' || rule.skipReason !== 'incomplete_data') continue
    const def = hatch.ruleDefs[rule.ruleId]
    out.push({
      level: 'warn',
      message: `Missing pet data blocked this booking. Rule ${rule.ruleId}${def ? ` ("${def.text}")` : ''} could not be checked${rule.skipDetail ? ` \u2014 ${rule.skipDetail}` : ''}. Fill in the missing data to unblock.`,
    })
  }

  for (const rule of hatch.rules) {
    if (rule.status !== 'matched' || !rule.affectedOffers?.length) continue
    for (const target of rule.affectedOffers) {
      const offer = hatch.offers.find((o) => o.offerId === target.id)
      if (!offer) continue
      if (offer.availability.baselineAvailable === false && !offer.changed) {
        out.push({
          level: 'warn',
          message: `Rule ${rule.ruleId} matched "${offer.offerName}" but Goose had already blocked it \u2014 the Hatch rule had no effect here.`,
        })
      }
    }
  }

  return out
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SESSION REGISTRY
   ═══════════════════════════════════════════════════════════════════════════════ */

function recordRules(location: string, hatch: HatchBlock, results: SearchResultLike[]): void {
  let known = ruleRegistry.get(location)
  if (!known) {
    known = new Map()
    ruleRegistry.set(location, known)
  }
  for (const [ruleId, def] of Object.entries(hatch.ruleDefs ?? {})) {
    known.set(ruleId, def)
  }

  const primaryIds = new Set(results.filter((r) => (r.offer?.type ?? 'PRIMARY') === 'PRIMARY').map((r) => r.offer?.id))
  let addons = addonRuleIds.get(location)
  if (!addons) {
    addons = new Set()
    addonRuleIds.set(location, addons)
  }
  for (const rule of hatch.rules) {
    if (rule.status !== 'matched' || !rule.affectedOffers?.length) continue
    if (rule.affectedOffers.some((o) => !primaryIds.has(o.id))) addons.add(rule.ruleId)
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   RESPONSE HISTORY — stores recent responses for on-demand detailed inspection
   ═══════════════════════════════════════════════════════════════════════════════ */

interface StoredReport {
  input: HatchDiagnosticsContext
  response: unknown
  timestamp: number
  header: string
}

const MAX_HISTORY = 20
const history: StoredReport[] = []
let lastReadIndex = 0 // index into history of the next unread entry

/**
 * Print all searches that arrived since the last hatch() call (drain-on-read).
 * If called with a numeric argument, re-prints that specific entry from the full
 * buffer (1-indexed, most recent = highest number).
 *
 * Called from the console via `hatch()` / `hatch(n)`.
 */
export async function printDetailed(index?: number): Promise<void> {
  if (history.length === 0) {
    console.log('%c\u{1F423} HATCH%c \u00B7 No searches recorded yet.', STYLE_HATCH, STYLE_DIM)
    return
  }

  // hatch(n) — re-read a specific entry by index (1-indexed)
  if (index != null && index > 0) {
    if (index > history.length) {
      console.log(
        `%c\u{1F423} HATCH%c \u00B7 Only ${history.length} entries in the buffer. Use hatch(1)..hatch(${history.length}).`,
        STYLE_HATCH,
        STYLE_DIM
      )
      return
    }
    const entry = history[index - 1]
    const loading = loadSettingsFor([entry])
    if (loading) await loading
    console.log(`%c\u{1F423} HATCH%c \u00B7 Re-reading entry ${index} of ${history.length}`, STYLE_HATCH, STYLE_DIM)
    printFullReport(entry.input, entry.response)
    return
  }

  // hatch() — drain: print everything since the last call. The cursor moves
  // before the settings load, so a second call while loading doesn't reprint.
  const pending = history.slice(lastReadIndex)
  lastReadIndex = history.length

  if (pending.length === 0) {
    console.log('%c\u{1F423} HATCH%c \u00B7 No new searches since last hatch() call.', STYLE_HATCH, STYLE_DIM)
    return
  }

  const loading = loadSettingsFor(pending)
  if (loading) await loading

  console.log(
    `%c\u{1F423} HATCH%c \u00B7 ${pending.length} search${pending.length === 1 ? '' : 'es'} since last call (${history.length} total in buffer)`,
    STYLE_HATCH,
    STYLE_DIM
  )

  for (const entry of pending) {
    printFullReport(entry.input, entry.response)
  }
}

/**
 * Load the waitlist settings for every report that contains a Hatch waitlist the
 * booking portal could act on. Fetched fresh on each hatch() call, so the output
 * reflects the current settings. Never throws.
 */
function loadSettingsFor(entries: StoredReport[]): Promise<void> | undefined {
  const toLoad = new Map<string, StoredReport>()
  for (const entry of entries) {
    if (needsWaitlistSettings(entry.input, entry.response)) toLoad.set(settingsKey(entry.input), entry)
  }
  // Nothing to fetch: return undefined so hatch() prints synchronously, as before.
  if (toLoad.size === 0) return undefined
  return Promise.all([...toLoad.values()].map((entry) => loadWaitlistSettings(entry.input, entry.response))).then(
    () => undefined
  )
}

/** True when the report has a Hatch waitlist whose outcome depends on the location settings. */
function needsWaitlistSettings(input: HatchDiagnosticsContext, response: unknown): boolean {
  if (waitlistNotPossible(input)) return false
  const results = (response as ResponseLike | undefined)?.results ?? []
  return results.some((result) => collectActionHits(result).hits.some((hit) => hit.flavor === 'waitlist'))
}

/** The full detailed report — only called on demand via hatch(). */
function printFullReport(input: HatchDiagnosticsContext, response: unknown): void {
  try {
    const resp = response as ResponseLike | undefined
    const hatch = resp?.hatch
    const results = resp?.results ?? []

    if (!hatch) {
      console.log(
        `%c\u{1F423} HATCH%c \u00B7 ${input.serviceTypeName} \u00B7 no sidecar (not routed through Hatch)`,
        STYLE_HATCH,
        STYLE_DIM
      )
      return
    }

    if (!hatch.evaluated) {
      console.log(
        `%c\u{1F423} HATCH%c \u00B7 ${input.serviceTypeName} \u00B7 no rules deployed \u2014 the response is the Goose baseline.`,
        STYLE_HATCH,
        STYLE_DIM
      )
      return
    }

    const dayMap = new Map<SearchResultLike, DayVerdict[] | null>()
    for (const result of results) {
      const hatchOffer = hatch.offers.find((o) => o.offerId === result.offer?.id)
      dayMap.set(result, collectDayVerdicts(result, hatchOffer, input.params?.startDate))
    }

    const states = results.map((r) => offerState(r, dayMap.get(r) ?? null))
    const blockedCount = states.filter((s) => s === 'blocked').length
    const partialCount = states.filter((s) => s === 'partial').length
    const ruleBlockedDays = results.reduce((total, result) => {
      const days = dayMap.get(result)
      if (!days) return total
      return total + days.filter((d) => !d.available && d.isHatch).length
    }, 0)

    console.group(
      `%c\u{1F423} HATCH%c \u00B7 ${buildHeader(input, hatch, blockedCount, partialCount, ruleBlockedDays, summarizeActions(input, results))}`,
      STYLE_HATCH,
      ''
    )

    const verdict = waitlistVerdict(input)
    printCustomerView(results, hatch, dayMap, verdict)
    printBlockedOffers(results, hatch, dayMap, input.apiType, verdict)
    printHatchActions(input, results, hatch, dayMap, verdict)
    printDayBreakdown(results, hatch, dayMap)
    printAvailableOffers(results, hatch, dayMap)
    printPricing(hatch, input.apiType)
    printRulesFired(hatch, displayNameIndex(results))
    printRulesSkipped(hatch)
    printRulesThatDidNothing(input.location, hatch, input.params?.type)

    console.groupCollapsed('%c\u{1F527} Raw sidecar', STYLE_DIM)
    console.log(hatch)
    console.groupEnd()

    console.groupEnd()
  } catch {
    // Diagnostics must never break anything.
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   REPORT ENTRY POINT (always-on: one-line summary only)
   ═══════════════════════════════════════════════════════════════════════════════ */

function report(input: HatchDiagnosticsContext, response: unknown): void {
  try {
    if (typeof console === 'undefined' || typeof console.groupCollapsed !== 'function') return

    const resp = response as ResponseLike | undefined
    const hatch = resp?.hatch
    const results = resp?.results ?? []

    resetIfLocationChanged(input.location)

    if (!hatch) {
      const key = `${input.serviceTypeName}:${input.endpoint}:${input.params?.type ?? ''}:nosidecar`
      if (printedHashes.get(key) !== 'x') {
        printedHashes.set(key, 'x')
        console.log(
          `%c\u{1F423} HATCH%c \u00B7 ${input.serviceTypeName} \u00B7 no sidecar on the response (this location is not routed through Hatch)`,
          STYLE_HATCH,
          STYLE_DIM
        )
      }
      return
    }

    const hash = hashReport(hatch, results)
    const dedupeKey = [
      input.serviceTypeName,
      input.endpoint,
      input.params?.type ?? '',
      input.parentOffer ?? '',
      input.params?.startDate ?? '',
      input.params?.endDate ?? '',
    ].join(':')
    const previous = printedHashes.get(dedupeKey)
    if (previous === hash) return
    printedHashes.set(dedupeKey, hash)

    recordRules(input.location, hatch, results)

    // Compute the one-line summary for the always-on output.
    const dayMap = new Map<SearchResultLike, DayVerdict[] | null>()
    for (const result of results) {
      const hatchOffer = hatch.offers.find((o) => o.offerId === result.offer?.id)
      dayMap.set(result, collectDayVerdicts(result, hatchOffer, input.params?.startDate))
    }
    const states = results.map((r) => offerState(r, dayMap.get(r) ?? null))
    const blockedCount = states.filter((s) => s === 'blocked').length
    const partialCount = states.filter((s) => s === 'partial').length
    const ruleBlockedDays = results.reduce((total, result) => {
      const days = dayMap.get(result)
      if (!days) return total
      return total + days.filter((d) => !d.available && d.isHatch).length
    }, 0)

    const header = buildHeader(
      input,
      hatch,
      blockedCount,
      partialCount,
      ruleBlockedDays,
      summarizeActions(input, results)
    )

    // Problems still print immediately — they should not be missed.
    const problems = collectProblems(hatch)
    for (const problem of problems) {
      console.log(
        `%c${problem.level === 'problem' ? '\u{1F534}' : '\u26A0\uFE0F'} HATCH%c \u00B7 ${problem.message}`,
        problem.level === 'problem' ? STYLE_PROBLEM : STYLE_WARN,
        ''
      )
    }

    // One-line summary — always visible. Type hatch() in the console for details.
    console.log(`%c\u{1F423} HATCH%c \u00B7 ${header}`, STYLE_HATCH, '')

    // Store for on-demand inspection.
    history.push({ input, response, timestamp: Date.now(), header })
    if (history.length > MAX_HISTORY) {
      history.shift()
      // Adjust the read cursor — one entry was evicted from the front
      if (lastReadIndex > 0) lastReadIndex--
    }
  } catch {
    // Diagnostics must never break a booking search.
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   GLOBAL hatch() FUNCTION — type in the console for detailed output
   ═══════════════════════════════════════════════════════════════════════════════ */

if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).hatch = (index?: number) => {
    // Async when it has to load waitlist settings; nothing is returned so the
    // console doesn't echo a pending Promise.
    void printDetailed(index)
  }
}
